import {PermissionName} from './permission-name.enum.js';

/** CRUD + Action axis for the matrix operation filter. 'action' = domain verbs
 * that aren't plain CRUD (purchase, export, import, transfer, activate, refund,
 * suspend, assign, report access, …). */
export type PermissionOperation = 'read' | 'create' | 'update' | 'delete' | 'action';

/** 'internal' = a Vera-staff-only permission, hidden from the company Roles &
 * Permissions grid (it belongs on the internal dashboard). Granted to platform
 * roles only. Undefined = company-scope (shown in the company grid). */
export type PermissionScope = 'internal';

export interface PermissionMeta {
  name: PermissionName;
  label: string;
  operation: PermissionOperation;
  scope?: PermissionScope;
  /** One-line explanation shown in the matrix info-tooltip (§25 keeps the
   * label itself short — longer detail goes here instead). Optional: only
   * permissions that have been backfilled carry it today. */
  description?: string;
}

export interface PermissionGroup {
  key: string;
  label: string;
  /** When 'internal', the entire group is Vera-staff-only and excluded from the
   * company matrix. */
  scope?: PermissionScope;
  /** For internal groups: the sidebar surface this group maps to (e.g. 'packages',
   * 'orders', 'companies'). The internal matrix sorts groups by the sidebar order of
   * these surfaces, so reordering the sidebar reorders the groups. */
  surface?: string;
  permissions: PermissionMeta[];
}

/**
 * Canonical grouping of all PermissionName values for the Permissions matrix UI.
 * Order within each group and between groups is intentional (display order).
 *
 * TD-044 reference: this metadata satisfies the grouping-metadata need identified
 * during VERA-780 planning.
 */
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    key: 'analytics',
    label: "Analytics",
    permissions: [
      {name: PermissionName.ViewAnalytics, label: "View analytics", operation: 'read'},
      {name: PermissionName.AnalyticsExport, label: "Export analytics", operation: 'action'},
      {name: PermissionName.AnalyticsGeneral, label: "View general report", operation: 'read'},
      {name: PermissionName.AnalyticsRoleLogistics, label: "View logistics report", operation: 'read'},
      {name: PermissionName.AnalyticsRoleLegal, label: "View legal report", operation: 'read'},
      {name: PermissionName.AnalyticsRoleFinance, label: "View finance report", operation: 'read'},
      {name: PermissionName.AnalyticsRoleMarketing, label: "View marketing report", operation: 'read'},
      {name: PermissionName.AnalyticsRoleCompliance, label: "View compliance report", operation: 'read'},
      // VRG-459. Label is 21 characters, inside the 260px matrix column (§25).
      {name: PermissionName.AnalyticsInputEdit, label: "Edit analytics inputs", operation: 'update', description: "Set the figures analytics metrics are calculated from, such as annual return-fraud loss and gross margin."},
    ],
  },
  {
    key: 'tags',
    label: "Tags",
    permissions: [
      {name: PermissionName.TagsInventoryView, label: "View inventory", operation: 'read', description: "See tag inventory levels (incoming + on hand)."},
      {name: PermissionName.TagsInventoryEdit, label: "Edit inventory", operation: 'update', description: "Adjust inventory levels and supply thresholds."},
      // VRG-140 Slice 1 (VERA-891): new — receipt-scan a package to receive
      // its tags into on-hand inventory (default: company_admin/logistics/purchasing).
      {name: PermissionName.TagsInventoryReceive, label: "Receive inventory", operation: 'action', description: "Receipt-scan a package to receive its tags into on-hand inventory."},
      {name: PermissionName.TagsInventorySettings, label: "Configure inventory", operation: 'update', description: "Configure inventory/auto-order settings."},
      {name: PermissionName.TagsAutoOrder, label: "Enable auto-reorder", operation: 'action', description: "Enable/trigger automatic tag re-ordering."},
      {name: PermissionName.TagsShopView, label: "View shop", operation: 'read'},
      {name: PermissionName.TagsShopPurchase, label: "Purchase tags", operation: 'action'},
      {name: PermissionName.TagsOrderView, label: "View tag orders", operation: 'read'},
      {name: PermissionName.TagsOrderExport, label: "Export tag orders", operation: 'action'},
    ],
  },
  {
    key: 'products',
    label: "Products",
    permissions: [
      {name: PermissionName.ManageProduct, label: "Manage products", operation: 'update'},
      {name: PermissionName.ManageStore, label: "Manage store", operation: 'update'},
      {name: PermissionName.ProductView, label: "View products", operation: 'read'},
      {name: PermissionName.ProductCreate, label: "Create products", operation: 'create'},
      {name: PermissionName.ProductEdit, label: "Edit products", operation: 'update'},
      {name: PermissionName.ProductDelete, label: "Delete products", operation: 'delete'},
      {name: PermissionName.ProductExport, label: "Export products", operation: 'action'},
      {name: PermissionName.ProductImport, label: "Import products", operation: 'action'},
    ],
  },
  {
    key: 'certificates',
    label: "Certificates",
    permissions: [
      {name: PermissionName.ViewCertificates, label: "View certificates", operation: 'read', description: "See the certificates list and open any certificate's details."},
      {name: PermissionName.ViewProvenanceHistory, label: "View provenance", operation: 'read', description: "See a certificate's full ownership history, including previous owners."},
      {name: PermissionName.ManageCertificate, label: "Manage certificates", operation: 'update', description: "Create and edit certificates. This is the permission that gates certificate creation."},
      // VRG-282: new — bind a package of tags to a product from the order page.
      // Seeded ON wherever manage_certificate is ON (company_admin, company_team_member,
      // platform_admin, platform_manager).
      {name: PermissionName.CertificateCreateFromPackage, label: "Create from package", operation: 'action', description: "Bind a delivered package of tags to a product, creating one certificate per tag. If the company activates certificates on creation, they are active and billed immediately."},
      // VRG-455: new — record a sale to a named buyer with no certificate selected.
      // Seeded ON wherever manage_certificate is ON (company_admin, company_team_member,
      // platform_admin, platform_manager), the same set as "Create from package" above.
      //
      // Label is "Assign to buyer" (user decision, amending the original "Record a sale"
      // proposal) — ONLY the label changed. The permission NAME (record_sale /
      // PermissionName.RecordSale), its catalog UUID and the seed/migration grants are
      // unchanged: renaming the label is cheap, renaming the permission is not.
      {name: PermissionName.RecordSale, label: "Assign to buyer", operation: 'action', description: "Record a sale to a buyer by email without selecting a certificate. The buyer's own scan resolves which item they have and completes the transfer."},
      // VRG-491: label kept to two words for the matrix's fixed 260px permission column
      // (§25) — the precision lives in the description, which the matrix shows on hover.
      {name: PermissionName.CertificateAcknowledgeInvalidScans, label: "Acknowledge scans", operation: 'action', description: "Mark a certificate's invalid scans as reviewed, clearing the invalid-scan indicator. Any invalid scan recorded after the acknowledgement raises it again."},
      {name: PermissionName.CertificateReplaceTag, label: "Replace tags", operation: 'action', description: "Swap a certificate's NFC tag for a different one, for example after damage."},
      {name: PermissionName.CertificateActivate, label: "Activate certificates", operation: 'action', description: "Make a created certificate live so it can be scanned and claimed."},
      // VRG-591 — label is two words for the 260px matrix column (§25); the
      // precision lives in the description, which the matrix shows on hover.
      // Grouped under Certificates rather than Billing on purpose: it is a
      // privacy control, and the company-level toggles only sit behind a billing
      // permission because they share a table with the activation trigger.
      {name: PermissionName.CertificateAccessSettings, label: "Access settings", operation: 'update', description: "Override the company's certificate-access defaults on a single product or certificate: whether a visitor must give their email to view it, and whether their location is collected."},
      // VRG-273 / VERA-455: internal-scoped — granted platform_admin-only (never to a
      // company role, see seed.ts). Showing it in a brand's role editor would offer a
      // capability no one in that company can ever hold, and hard-deleting a certificate
      // cascades away scans + certificate_metering rows (VRG-274), which is Vera-staff
      // cleanup, not a brand operation. The write-side allowlist (company-custom-role
      // use-cases) rejects any attempt to grant this via a custom role/override.
      {name: PermissionName.CertificateDelete, label: "Delete certificates", operation: 'delete', scope: 'internal', description: "Permanently remove a certificate. The underlying product is not deleted."},
      {name: PermissionName.TransferCertificate, label: "Transfer certificates", operation: 'action', description: "Move ownership of a certificate to another owner on the brand's behalf."},
      // VERA-931 / VRG-528. Label is 15 characters — well inside the matrix's fixed 260px
      // first column (§25) — and it is the literal label of the menu item it gates, so the
      // row and the button read the same. See the enum for why receipt is its own
      // permission and dispatch is not.
      {name: PermissionName.CustodyReceive, label: "Confirm arrival", operation: 'action', description: "Confirm a dispatched certificate arrived at one of the company's stores. If the company activates at location, this is what makes the certificate live and billable."},
      {name: PermissionName.CertificateExport, label: "Export certificates", operation: 'action', description: "Download the certificates list as a file."},
      // §25 — "Manage groups", not "Manage Certificate Groups" (25 chars, over the
      // matrix's 260px first column). The word "certificate" is redundant here: the
      // matrix renders this row under its collapsible "Certificates" section header,
      // so the qualifier is already on screen. Full detail lives in the description.
      {name: PermissionName.ManageCertificateGroups, label: "Manage groups", operation: 'action', description: "Manage certificate groups: link components into a parent certificate, and split divisible groups."},
    ],
  },
  {
    key: 'owners',
    label: "Owners",
    permissions: [
      {name: PermissionName.ViewCustomers, label: "View owners", operation: 'read'},
      {name: PermissionName.InviteOwner, label: "Invite owners", operation: 'action'},
      {name: PermissionName.OwnerCreate, label: "Create owners", operation: 'create'},
      {name: PermissionName.OwnerEdit, label: "Edit owners", operation: 'update'},
      {name: PermissionName.DeleteCustomers, label: "Delete owners", operation: 'delete'},
      {name: PermissionName.OwnerExport, label: "Export owners", operation: 'action'},
      {name: PermissionName.OwnerImport, label: "Import owners", operation: 'action'},
    ],
  },
  {
    key: 'studio',
    label: "Studio",
    permissions: [
      {name: PermissionName.StudioView, label: "View studio", operation: 'read'},
      {name: PermissionName.StudioCreate, label: "Create in studio", operation: 'create'},
      {name: PermissionName.StudioEdit, label: "Edit in studio", operation: 'update'},
      {name: PermissionName.StudioDelete, label: "Delete in studio", operation: 'delete'},
    ],
  },
  {
    key: 'ai',
    label: "AI",
    permissions: [
      // VRG-227: Ask Vera gates its /answer(/stream) endpoints on this permission
      // (ask.controller.ts). It was seeded onto roles (seed.ts) from the start, but
      // commented out of this grid — so it could never actually be granted or revoked
      // from the role editor, only ever left at its seed default. Restored so it's a
      // real, editable permission rather than a promise the UI couldn't keep.
      {name: PermissionName.AiView, label: "View AI", operation: 'read'},
      // AiUseCredits / AiPurchaseCredits: no credits/billing surface exists yet, so
      // surfacing them here would offer a capability with no effect anywhere in the
      // app. Left commented until that ships. The enum/seed values still exist.
      // {name: PermissionName.AiUseCredits, label: "Use AI credits", operation: 'action'},
      // {name: PermissionName.AiPurchaseCredits, label: "Buy AI credits", operation: 'action'},
    ],
  },
  {
    key: 'company',
    label: "Company",
    permissions: [
      {name: PermissionName.ViewCompany, label: "View company", operation: 'read'},
      {name: PermissionName.ManageCompany, label: "Manage company", operation: 'update'},
      // VRG-512. Label is 18 characters, inside the 260px matrix column (§25) — the
      // "for now, admins only" reasoning is too long for a label and lives in the
      // enum and at the API gate instead.
      {name: PermissionName.SampleDataManage, label: "Manage sample data", operation: 'action', description: "Add or clear the labelled sample products, certificates and scans used to demonstrate an empty platform. Samples are never billed."},
      // VRG-654. "Refer & earn" (VRG-680 rename) is 12 characters (§25).
      {name: PermissionName.ReferralsSend, label: "Refer & earn", operation: 'action', description: "Share your referral link and invite other brands to Vera. Your company earns activations when a brand you referred pays."},
      // ViewCompanies / DeleteCompany are internal-scoped — they live in the internal
      // "Companies" page group below (page-based internal grouping), not the company tab.
    ],
  },
  {
    key: 'users',
    label: "Users",
    permissions: [
      {name: PermissionName.UsersView, label: "View users", operation: 'read'},
      {name: PermissionName.UsersInvite, label: "Invite users", operation: 'create'},
      {name: PermissionName.UsersEdit, label: "Edit users", operation: 'update'},
      // 'update', not 'create': nothing new exists afterwards — an existing user's
      // credential is being re-issued. Label kept to two words for the 260px matrix
      // column (§25).
      {name: PermissionName.UsersResetPassword, label: "Reset passwords", operation: 'update'},
      {name: PermissionName.UsersDelete, label: "Remove users", operation: 'delete'},
    ],
  },
  {
    key: 'roles',
    label: "Roles & Permissions",
    permissions: [
      {name: PermissionName.ViewRoles, label: "View roles", operation: 'read'},
      {name: PermissionName.ManageRoles, label: "Manage roles", operation: 'update'},
      {name: PermissionName.AssignUsers, label: "Assign users", operation: 'action'},
    ],
  },
  {
    key: 'billing',
    label: "Billing",
    permissions: [
      {name: PermissionName.BillingView, label: "View billing", operation: 'read'},
      {name: PermissionName.BillingActivationSettings, label: "Manage activation", operation: 'update'},
      {name: PermissionName.BillingPurchaseAddons, label: "Purchase add-ons", operation: 'action'},
      {name: PermissionName.BillingEditAddons, label: "Edit add-ons", operation: 'update'},
      {name: PermissionName.BillingChangePlan, label: "Change plan", operation: 'update'},
      {name: PermissionName.BillingEndPlan, label: "End plan", operation: 'delete'},
      {name: PermissionName.BillingInvoiceView, label: "View invoices", operation: 'read'},
      {name: PermissionName.BillingInvoiceExport, label: "Export invoices", operation: 'action'},
      {name: PermissionName.BillingNotifications, label: "Receive notifications", operation: 'action'},
      {name: PermissionName.PaymentMethodView, label: "View payments", operation: 'read'},
      {name: PermissionName.PaymentMethodCreate, label: "Add payments", operation: 'create'},
      {name: PermissionName.PaymentMethodEdit, label: "Edit payments", operation: 'update'},
      {name: PermissionName.PaymentMethodDelete, label: "Delete payments", operation: 'delete'},
    ],
  },
  {
    key: 'address_book',
    label: "Address Book",
    permissions: [
      {name: PermissionName.AddressView, label: "View addresses", operation: 'read'},
      {name: PermissionName.AddressCreate, label: "Add addresses", operation: 'create'},
      {name: PermissionName.AddressEdit, label: "Edit addresses", operation: 'update'},
      {name: PermissionName.AddressDelete, label: "Delete addresses", operation: 'delete'},
    ],
  },
  {
    key: 'installation',
    label: "Installation",
    permissions: [
      {name: PermissionName.InstallationView, label: "View installation", operation: 'read', description: "See the install snippet, subdomain, and which destination tag scans resolve to."},
      {name: PermissionName.InstallationEdit, label: "Edit installation", operation: 'update', description: "Add or change the install URL and connect an ecommerce platform."},
      {name: PermissionName.InstallationSetPrimary, label: "Set scan destination", operation: 'action', description: "Choose which install tag scans resolve to. Affects every tag already in the wild."},
      {name: PermissionName.InstallationEditSubdomain, label: "Edit subdomain", operation: 'update', description: "Rename the company's Vera subdomain. Changes its public address and the destination of its default install."},
    ],
  },
  {
    key: 'integrations',
    label: "Integrations",
    permissions: [
      {name: PermissionName.IntegrationView, label: "View integrations", operation: 'read'},
      {name: PermissionName.IntegrationCreate, label: "Add integrations", operation: 'create'},
      {name: PermissionName.IntegrationEdit, label: "Edit integrations", operation: 'update'},
      {name: PermissionName.IntegrationDelete, label: "Delete integrations", operation: 'delete'},
    ],
  },
  {
    key: 'field_mapping',
    // "Fields", not "Field Mapping" — the section this group gates was renamed when
    // Defaults joined Mapping under one page, and a group still called Field Mapping
    // would make the two rows below it look like they belong to something else.
    label: "Fields",
    permissions: [
      {name: PermissionName.FieldMappingView, label: "View mappings", operation: 'read'},
      {name: PermissionName.FieldMappingCreate, label: "Add mappings", operation: 'create'},
      {name: PermissionName.FieldMappingEdit, label: "Edit mappings", operation: 'update'},
      {name: PermissionName.FieldMappingDelete, label: "Delete mappings", operation: 'delete'},
      // §25 — both fit the matrix's 260px first column at the same length as the
      // mapping rows they sit beside ("View defaults" is a character shorter than
      // "View mapping" once you count the word, and measures narrower on screen).
      {name: PermissionName.FieldDefaultView, label: "View defaults", operation: 'read'},
      {name: PermissionName.FieldDefaultEdit, label: "Edit defaults", operation: 'update'},
    ],
  },
  {
    key: 'activity_log',
    label: "Activity Log",
    permissions: [
      {name: PermissionName.ActivityLogView, label: "View activity", operation: 'read'},
      {name: PermissionName.ActivityLogExport, label: "Export activity", operation: 'action'},
    ],
  },
  {
    // VRG-226. Note what is NOT here: opening a request, and reading the thread on
    // a request you opened yourself. Both are ungated on purpose — a permission
    // that can stop someone asking for help breaks the system exactly when it is
    // needed, and the request is already scoped to its author.
    key: 'support',
    label: "Support",
    permissions: [
      {
        name: PermissionName.SupportRequestsViewCompany,
        label: "View company requests",
        operation: 'read',
        description: "See every support request raised by anyone at your company, not just your own.",
      },
      {
        name: PermissionName.SupportRequestsVote,
        label: "Vote on requests",
        operation: 'action',
        description: "Upvote feature requests and integrations to influence what Vera builds next.",
      },
    ],
  },
  // ── Internal (Vera staff) permission groups, BROKEN OUT BY PAGE/SURFACE ────────
  // Order mirrors the internal sidebar (Sidebar.tsx INTERNAL_MENU): Packages, Orders,
  // Invoices, Companies, Subscriptions, Users (+ Analytics). The `surface` key on each
  // group ties it to its sidebar surface; the internal matrix sorts groups by that order
  // (useInternalRolesMatrix.ts → INTERNAL_SIDEBAR_SURFACE_ORDER), so reordering the
  // sidebar order there reorders these groups without touching this list.
  {
    key: 'internal_packages', label: "Packages", scope: 'internal', surface: 'packages',
    permissions: [
      {name: PermissionName.InternalPackageView, label: "View packages", operation: 'read', scope: 'internal', description: "View internal packages."},
      {name: PermissionName.InternalPackageUpload, label: "Upload packages", operation: 'create', scope: 'internal', description: "Create/encode packages, resolve handles, read a package's tags."},
      // VRG-761 — label kept to 15 chars so it fits the matrix's 260px first column (§25).
      {name: PermissionName.InternalPackageDelete, label: "Delete packages", operation: 'delete', scope: 'internal', description: "Delete a package (box). Tags inside are detached and kept; only never-used tags can be deleted, and only on request."},
      {name: PermissionName.InternalAssignDirectly, label: "Assign to companies", operation: 'update', scope: 'internal', description: "Assign tags or a whole package straight to a company without an order. Decides who may scan that stock."},
      {name: PermissionName.InternalTagRetire, label: "Retire tags", operation: 'action', scope: 'internal', description: "Retire / reactivate encoded tags."},
    ],
  },
  {
    key: 'internal_orders', label: "Orders", scope: 'internal', surface: 'orders',
    permissions: [
      {name: PermissionName.InternalOrderView, label: "View orders", operation: 'read', scope: 'internal', description: "View Shopify orders in the internal platform."},
      {name: PermissionName.InternalOrderLink, label: "Link packages", operation: 'action', scope: 'internal', description: "Link packages to orders, fulfill shipments, buy labels, assign/ship packages."},
    ],
  },
  {
    key: 'internal_invoices', label: "Invoices", scope: 'internal', surface: 'invoices',
    permissions: [
      // VRG-426 — the read half of this surface, which had no permission at all until now.
      // Listed first so the group reads view → act, matching every other internal group.
      {name: PermissionName.InternalInvoiceView, label: "View invoices", operation: 'read', scope: 'internal', description: "View Stripe invoices across every company in one place."},
      {name: PermissionName.InternalCompanyIssueRefund, label: "Issue refunds", operation: 'action', scope: 'internal'},
      {name: PermissionName.InternalIntegrationsView, label: "View integrations", operation: 'read', scope: 'internal'},
    ],
  },
  {
    key: 'internal_companies', label: "Companies", scope: 'internal', surface: 'companies',
    permissions: [
      {name: PermissionName.ViewCompanies, label: "View all companies", operation: 'read', scope: 'internal'},
      {name: PermissionName.InternalCompanyCreate, label: "Create companies", operation: 'create', scope: 'internal'},
      {name: PermissionName.DemoProvision, label: "Provision demo", operation: 'create', scope: 'internal', description: "Create an acquisition demo company from a prospect's website, and issue its demo certificate for cold outbound."},
      {name: PermissionName.InternalCompanyEnterSpace, label: "Enter company space", operation: 'action', scope: 'internal'},
      {name: PermissionName.InternalCompanyAddCertCredits, label: "Add cert credits", operation: 'action', scope: 'internal'},
      {name: PermissionName.InternalCompanyAddAiCredits, label: "Add AI credits", operation: 'action', scope: 'internal'},
      {name: PermissionName.InternalCompanySuspend, label: "Suspend companies", operation: 'action', scope: 'internal'},
      {name: PermissionName.DeleteCompany, label: "Delete companies", operation: 'delete', scope: 'internal'},
    ],
  },
  {
    key: 'internal_subscriptions', label: "Subscriptions", scope: 'internal', surface: 'subscriptions',
    permissions: [
      {name: PermissionName.InternalSubscriptionView, label: "View subscriptions", operation: 'read', scope: 'internal'},
      {name: PermissionName.InternalCompanyChangeSubscription, label: "Change subscription", operation: 'update', scope: 'internal'},
    ],
  },
  {
    key: 'internal_users', label: "Users", scope: 'internal', surface: 'users',
    permissions: [
      {name: PermissionName.ViewPlatformSupervisors, label: "View internal users", operation: 'read', scope: 'internal'},
    ],
  },
  {
    // VRG-226. One permission covers reading the cross-company queue AND acting on
    // it (reply, set status, curate the board). Splitting read from write would be
    // a distinction without a role: nobody on the internal side needs to read the
    // support queue without being able to answer it, and every extra row in this
    // matrix is a decision someone has to make about a case that does not exist.
    key: 'internal_support', label: "Support", scope: 'internal', surface: 'support',
    permissions: [
      {name: PermissionName.InternalSupportManage, label: "Manage support", operation: 'action', scope: 'internal', description: "Read, reply to and set the status of support requests from every company, and curate the boards."},
    ],
  },
  {
    // VRG-221 — "What's new". Its OWN group rather than a second row inside
    // `internal_support`, because it is its own sidebar surface: the matrix sorts
    // groups by `surface` against the internal sidebar order, so folding it into
    // support would file the announcements toggle under the wrong page.
    //
    // One permission, same reasoning as internal_support: nobody needs to read the
    // announcement list without being able to publish, and separating "publish" from
    // "notify requesters" would let a staffer announce a shipped feature while being
    // unable to tell the people who asked for it — which is the exact half-finished
    // loop this surface exists to close.
    key: 'internal_announcements', label: "What's new", scope: 'internal', surface: 'announcements',
    permissions: [
      {name: PermissionName.InternalAnnouncementsManage, label: "Manage what's new", operation: 'action', scope: 'internal', description: "Write, publish and edit \"What's new\" announcements, and notify everyone who voted for or requested a feature that it shipped."},
    ],
  },
  {
    key: 'internal_analytics', label: "Analytics", scope: 'internal', surface: 'analytics',
    permissions: [
      {name: PermissionName.InternalAnalyticsView, label: "View analytics", operation: 'read', scope: 'internal'},
    ],
  },
  {
    // VRG-684 — GTM (go-to-market) as its own group, so the role editor shows it apart from
    // Analytics. One VIEW permission: writes on GTM pages are platform_admin by role today.
    key: 'internal_gtm', label: "GTM", scope: 'internal', surface: 'gtm',
    permissions: [
      {name: PermissionName.InternalGtmView, label: "View GTM", operation: 'read', scope: 'internal', description: "Open the GTM pages: Channels, SEO & AEO, Articles, Sales and Outreach, Trade shows and Affiliates."},
      // VRG-685 — publishing makes an article live on vera.inc within a minute, so it is its own grant.
      {name: PermissionName.InternalArticlesPublish, label: "Publish articles", operation: 'action', scope: 'internal', description: "Publish and unpublish vera.inc /insights articles and /learn pages from GTM → Articles. Goes live on the website within about a minute."},
      // VRG-691 — writes on the sales pipeline: leads, companies, deal stages, values and settings.
      {name: PermissionName.InternalSalesManage, label: "Manage sales", operation: 'action', scope: 'internal', description: "Add and edit leads and companies on GTM → Sales, move deals between stages, set a deal's value, and change the stage probabilities."},
      // VRG-740 — writes on GTM → Articles → Ideas and VERAfied guests. Not publishing (above).
      {name: PermissionName.InternalContentIdeasManage, label: "Manage ideas", operation: 'action', scope: 'internal', description: "Add and edit content ideas and VERAfied guests on GTM → Articles, and move an idea from idea to approved draft. Publishing an article is separate."},
    ],
  },
];

/**
 * VRG-273: the single derived source of "which permission names are internal-scoped" —
 * a permission is internal if it is directly marked `scope: 'internal'`, OR its group is
 * (internal groups mark both today, but this inherits regardless so the two can never
 * drift). Consumed by:
 * - the matrix READ filter (company-custom-role use-cases), so both stay in lockstep
 *   with this one definition instead of two independent `scope !== 'internal'` filters
 * - the custom-role permission WRITE allowlist (VRG-273 — the write previously accepted
 *   any permission id with no allowlist at all)
 * - the platform UI, to hide (not just disable) actions gated by an internal-only
 *   permission for non-internal users (working-conventions §27)
 */
export const INTERNAL_PERMISSIONS: ReadonlySet<PermissionName> = new Set(
  PERMISSION_GROUPS.flatMap(g =>
    g.permissions
      .filter(p => p.scope === 'internal' || g.scope === 'internal')
      .map(p => p.name),
  ),
);

export const isInternalPermission = (name: PermissionName | string): boolean =>
  INTERNAL_PERMISSIONS.has(name as PermissionName);
